(() => {
  const LOADER_MIN_DURATION = 0;
  const STORAGE = window.sessionStorage;
  const MANUAL_BLOCK_KEY = 'fg_manual_block_sig';
  const LAST_SIG_KEY = 'fg_last_sig';
  const LAST_HAD_GIFT_KEY = 'fg_last_had_gift';

  let busy = false;
  let pendingCheck = false;
  let pendingCheckSource = 'pending';
  let scheduledCheckTimeout = null;
  let internalRequestDepth = 0;

  function isDebugEnabled() {
    try {
      return new URLSearchParams(window.location.search).get('cart_debug') === '1';
    } catch (_error) {
      return false;
    }
  }

  function debugNow() {
    if (window.performance && typeof window.performance.now === 'function') {
      return window.performance.now();
    }

    return Date.now();
  }

  function startDebugRun(source) {
    if (!isDebugEnabled()) return null;

    return {
      source,
      startedAt: debugNow(),
      lastAt: debugNow(),
      steps: []
    };
  }

  function debugStep(run, label, meta) {
    if (!run) return;

    const now = debugNow();
    run.steps.push({
      label,
      totalMs: +(now - run.startedAt).toFixed(2),
      deltaMs: +(now - run.lastAt).toFixed(2),
      meta: meta || {}
    });
    run.lastAt = now;
  }

  function finishDebugRun(run, status, meta) {
    if (!run) return;

    debugStep(run, `end:${status}`, meta || {});

    try {
      console.groupCollapsed(`[free-gift-debug] reconcile ${status} ${run.steps[run.steps.length - 1].totalMs}ms (${run.source})`);
      console.log('source', run.source);
      if (meta && Object.keys(meta).length) {
        console.log('summary', meta);
      }
      console.table(run.steps.map((step) => ({
        step: step.label,
        totalMs: step.totalMs,
        deltaMs: step.deltaMs,
        meta: JSON.stringify(step.meta)
      })));
      console.groupEnd();
    } catch (_error) {}
  }

  function debugEvent(label, meta) {
    if (!isDebugEnabled()) return;

    try {
      console.log(`[free-gift-debug] ${label}`, meta || {});
    } catch (_error) {}
  }

  function isInternalRequestActive() {
    return internalRequestDepth > 0;
  }

  async function withInternalRequests(callback) {
    internalRequestDepth += 1;

    try {
      return await callback();
    } finally {
      internalRequestDepth = Math.max(internalRequestDepth - 1, 0);
    }
  }

  function readConfig() {
    const configEl = document.getElementById('free-gift-config');
    if (!configEl) return null;

    const enabled = configEl.dataset.freeGiftEnabled === 'true' || configEl.dataset.freeGiftEnabled === '1';
    const variantId = parseInt(configEl.dataset.freeGiftVariantId || '0', 10);
    const threshold = parseInt(configEl.dataset.freeGiftThreshold || '7500', 10);

    if (!enabled) return { enabled: false, variantId: 0, threshold: 0 };
    if (!variantId || !threshold) return null;

    return { enabled: true, variantId, threshold };
  }

  function scheduleCheck(delay, source) {
    const triggerSource = source || 'unknown';

    if (isInternalRequestActive()) {
      debugEvent('trigger-suppressed', { source: triggerSource, reason: 'internal-request' });
      return;
    }

    if (scheduledCheckTimeout) {
      clearTimeout(scheduledCheckTimeout);
    }

    const checkDelay = typeof delay === 'number' ? Math.max(delay, 0) : 0;
    scheduledCheckTimeout = setTimeout(() => {
      scheduledCheckTimeout = null;
      checkFreeGift(triggerSource);
    }, checkDelay);
  }

  function startDrawerLoading(minDuration) {
    try {
      document.dispatchEvent(new CustomEvent('cart:drawer-loading-start', {
        detail: { minDuration: typeof minDuration === 'number' ? minDuration : LOADER_MIN_DURATION }
      }));
    } catch (_error) {}
  }

  function stopDrawerLoading() {
    try {
      document.dispatchEvent(new CustomEvent('cart:drawer-loading-stop'));
    } catch (_error) {}
  }

  function signatureFromCart(cart, variantId) {
    try {
      const parts = cart.items
        .filter((item) => item.variant_id !== variantId)
        .map((item) => `${item.variant_id}:${item.quantity}`)
        .sort();

      return parts.join('|');
    } catch (_error) {
      return '';
    }
  }

  async function fetchCart() {
    try {
      const cached = window.__elanora_cart_cache;
      if (cached && (Date.now() - cached.ts) < 2000) {
        window.__elanora_cart_cache = null;
        return cached.data;
      }
    } catch (_) {}
    const response = await withInternalRequests(() => fetch('/cart.js', { credentials: 'same-origin' }));
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  function getAutoGiftLines(cart, variantId) {
    return cart.items.filter(
      (item) => item.variant_id === variantId
        && item.properties
        && (item.properties._free_gift === 'true' || item.properties._free_gift === true)
    );
  }

  function getAllGiftVariantLines(cart, variantId) {
    return cart.items.filter((item) => item.variant_id === variantId);
  }

  function findAutoGiftLineKey(cart, variantId) {
    const item = cart.items.find(
      (line) => line.variant_id === variantId
        && line.properties
        && (line.properties._free_gift === 'true' || line.properties._free_gift === true)
    );

    return item ? item.key : null;
  }

  function setLastState(sig, hasGift) {
    STORAGE.setItem(LAST_SIG_KEY, sig);
    STORAGE.setItem(LAST_HAD_GIFT_KEY, hasGift ? '1' : '0');
  }

  async function addGift(variantId) {
    if (STORAGE.getItem('fg_adding') === '1') return { added: false, sections: null };

    STORAGE.setItem('fg_adding', '1');

    try {
      const response = await withInternalRequests(() => fetch('/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: variantId,
          quantity: 1,
          properties: { _free_gift: 'true' },
          sections: GIFT_SECTIONS,
          sections_url: window.location.pathname
        })
      }));

      if (!response.ok) return { added: false, sections: null };
      try {
        const data = await response.json();
        return { added: true, sections: data.sections || null };
      } catch (_) {
        return { added: true, sections: null };
      }
    } finally {
      setTimeout(() => {
        try {
          STORAGE.removeItem('fg_adding');
        } catch (_error) {}
      }, 400);
    }
  }

  const GIFT_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  async function removeGift(lineKey) {
    const response = await withInternalRequests(() => fetch('/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: GIFT_SECTIONS,
        sections_url: window.location.pathname
      })
    }));
    if (!response.ok) return null;
    try { const data = await response.json(); return data.sections || null; } catch (_) { return null; }
  }

  async function changeGiftQty(lineKey, quantity) {
    const response = await withInternalRequests(() => fetch('/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: lineKey,
        quantity,
        sections: GIFT_SECTIONS,
        sections_url: window.location.pathname
      })
    }));
    if (!response.ok) return null;
    try { const data = await response.json(); return data.sections || null; } catch (_) { return null; }
  }

  async function refreshCartUI() {
    const sections = ['cart-drawer', 'cart-icon-bubble'];

    await withInternalRequests(async () => {
      try {
        const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`, { credentials: 'same-origin' });
        if (response.ok) {
          const data = await response.json();
          const drawerEl = document.querySelector('cart-drawer');

          if (drawerEl && typeof drawerEl.renderContents === 'function') {
            try {
              drawerEl.renderContents({ sections: data });
              return;
            } catch (_error) {}
          }
        }
      } catch (_error) {}

      try {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } catch (_error) {}
    });
  }

  function applyCartSections(sectionsData) {
    if (!sectionsData) return false;
    try {
      const drawerEl = document.querySelector('cart-drawer');
      if (drawerEl && typeof drawerEl.renderContents === 'function') {
        drawerEl.renderContents({ sections: sectionsData });
        return true;
      }
    } catch (_) {}
    return false;
  }

  function chooseAction(cart, variantId, threshold) {
    const total = cart.total_price || 0;
    const autoGiftLines = getAutoGiftLines(cart, variantId);
    const allGiftVariantLines = getAllGiftVariantLines(cart, variantId);
    const sig = signatureFromCart(cart, variantId);
    const present = autoGiftLines.length > 0;
    const presentAny = allGiftVariantLines.length > 0;
    const giftKey = autoGiftLines[0] ? autoGiftLines[0].key : findAutoGiftLineKey(cart, variantId);
    const lastHadGift = STORAGE.getItem(LAST_HAD_GIFT_KEY) === '1';
    const manualBlockSig = STORAGE.getItem(MANUAL_BLOCK_KEY);

    if (lastHadGift && !present && total >= threshold) {
      STORAGE.setItem(MANUAL_BLOCK_KEY, sig);
    } else if (manualBlockSig && manualBlockSig !== sig) {
      STORAGE.removeItem(MANUAL_BLOCK_KEY);
    }

    if (autoGiftLines.length > 1) {
      return {
        action: 'dedupe_auto_gifts',
        cart,
        sig,
        total,
        threshold,
        present,
        presentAny,
        autoGiftLines,
        allGiftVariantLines,
        giftKey
      };
    }

    if (autoGiftLines.length >= 1 && autoGiftLines[0].quantity > 1) {
      return {
        action: 'clamp_auto_gift_qty',
        cart,
        sig,
        total,
        threshold,
        present,
        presentAny,
        autoGiftLines,
        allGiftVariantLines,
        giftKey
      };
    }

    if (total >= threshold && !presentAny) {
      const blocked = STORAGE.getItem(MANUAL_BLOCK_KEY);

      if (!blocked || blocked !== sig) {
        return {
          action: 'add_gift',
          cart,
          sig,
          total,
          threshold,
          present,
          presentAny,
          autoGiftLines,
          allGiftVariantLines,
          giftKey
        };
      }
    } else if (total < threshold && giftKey) {
      return {
        action: 'remove_gift',
        cart,
        sig,
        total,
        threshold,
        present,
        presentAny,
        autoGiftLines,
        allGiftVariantLines,
        giftKey
      };
    }

    return {
      action: 'noop',
      cart,
      sig,
      total,
      threshold,
      present,
      presentAny,
      autoGiftLines,
      allGiftVariantLines,
      giftKey
    };
  }

  async function checkFreeGift(source) {
    const triggerSource = source || 'unknown';

    if (busy) {
      pendingCheck = true;
      pendingCheckSource = triggerSource;
      debugEvent('trigger-queued', { source: triggerSource, reason: 'busy' });
      return;
    }

    busy = true;
    pendingCheck = false;
    pendingCheckSource = 'pending';

    let overlayShown = false;
    let status = 'ok';
    let statusMeta = {};
    const debugRun = startDebugRun(triggerSource);

    const ensureLoading = () => {
      if (overlayShown) return;
      overlayShown = true;
      startDrawerLoading(LOADER_MIN_DURATION);
    };

    try {
      const config = readConfig();
      if (!config || !config.enabled) {
        statusMeta = { reason: 'disabled-or-missing-config' };
        return;
      }

      const { variantId, threshold } = config;

      debugStep(debugRun, 'config-ready', {
        variantId,
        threshold
      });

      const cartFetchStart = debugNow();
      const cart = await fetchCart();
      debugStep(debugRun, 'fetch-cart', {
        durationMs: +(debugNow() - cartFetchStart).toFixed(2)
      });

      const decision = chooseAction(cart, variantId, threshold);
      debugStep(debugRun, 'snapshot', {
        total: decision.total,
        threshold: decision.threshold,
        eligible: decision.total >= decision.threshold,
        giftPresent: decision.present,
        giftPresentAny: decision.presentAny,
        autoGiftCount: decision.autoGiftLines.length,
        allGiftCount: decision.allGiftVariantLines.length,
        signature: decision.sig
      });
      debugStep(debugRun, 'action-selected', {
        action: decision.action
      });

      if (decision.action === 'dedupe_auto_gifts') {
        ensureLoading();
        const mutationStart = debugNow();

        let dedupeGiftSections = null;
        for (let index = 1; index < decision.autoGiftLines.length; index += 1) {
          dedupeGiftSections = await removeGift(decision.autoGiftLines[index].key);
        }

        debugStep(debugRun, 'mutation', {
          action: decision.action,
          durationMs: +(debugNow() - mutationStart).toFixed(2),
          removedLines: Math.max(decision.autoGiftLines.length - 1, 0)
        });

        const refreshStart = debugNow();
        if (!applyCartSections(dedupeGiftSections)) await refreshCartUI();
        debugStep(debugRun, 'refresh-ui', {
          durationMs: +(debugNow() - refreshStart).toFixed(2)
        });

        setLastState(decision.sig, true);
        statusMeta = { action: decision.action };
        return;
      }

      if (decision.action === 'clamp_auto_gift_qty') {
        ensureLoading();
        const mutationStart = debugNow();

        const clampSections = await changeGiftQty(decision.autoGiftLines[0].key, 1);

        debugStep(debugRun, 'mutation', {
          action: decision.action,
          durationMs: +(debugNow() - mutationStart).toFixed(2)
        });

        const refreshStart = debugNow();
        if (!applyCartSections(clampSections)) await refreshCartUI();
        debugStep(debugRun, 'refresh-ui', {
          durationMs: +(debugNow() - refreshStart).toFixed(2)
        });

        setLastState(decision.sig, true);
        statusMeta = { action: decision.action };
        return;
      }

      if (decision.action === 'add_gift') {
        ensureLoading();
        const mutationStart = debugNow();
        const { added, sections: addSections } = await addGift(variantId);

        debugStep(debugRun, 'mutation', {
          action: decision.action,
          durationMs: +(debugNow() - mutationStart).toFixed(2),
          added
        });

        if (added) {
          STORAGE.removeItem(MANUAL_BLOCK_KEY);

          const refreshStart = debugNow();
          if (!applyCartSections(addSections)) await refreshCartUI();
          debugStep(debugRun, 'refresh-ui', {
            durationMs: +(debugNow() - refreshStart).toFixed(2)
          });
        }

        setLastState(decision.sig, added || decision.present);
        statusMeta = { action: decision.action, added };
        return;
      }

      if (decision.action === 'remove_gift') {
        ensureLoading();
        const mutationStart = debugNow();

        const removeSections = await removeGift(decision.giftKey);

        debugStep(debugRun, 'mutation', {
          action: decision.action,
          durationMs: +(debugNow() - mutationStart).toFixed(2)
        });

        const refreshStart = debugNow();
        if (!applyCartSections(removeSections)) await refreshCartUI();
        debugStep(debugRun, 'refresh-ui', {
          durationMs: +(debugNow() - refreshStart).toFixed(2)
        });

        setLastState(decision.sig, false);
        statusMeta = { action: decision.action };
        return;
      }

      setLastState(decision.sig, decision.present);
      statusMeta = { action: decision.action };
    } catch (error) {
      status = 'error';
      statusMeta = {
        message: error && error.message ? error.message : 'check failed'
      };
      console.warn('Free gift check failed', error);
    } finally {
      if (overlayShown) {
        stopDrawerLoading();
      }

      finishDebugRun(debugRun, status, statusMeta);
      busy = false;

      if (pendingCheck) {
        const nextSource = pendingCheckSource;
        pendingCheck = false;
        pendingCheckSource = 'pending';
        setTimeout(() => checkFreeGift(nextSource), 0);
      }
    }
  }

  const originalFetch = window.fetch.bind(window);
  window.fetch = function freeGiftFetchWrapper(input, init) {
    const url = typeof input === 'string' ? input : ((input && input.url) || '');
    const isAddEndpoint = /\/cart\/add(?:\.js)?(?:\?|$)/.test(url);
    const isCartEndpoint = /\/cart(?:\/(?:add|change|update|clear)(?:\.js)?|\.js)(?:\?|$)/.test(url);
    const requestStartedInternal = isInternalRequestActive();
    const result = originalFetch(input, init);

    if (isCartEndpoint && !requestStartedInternal) {
      result.then(() => {
        scheduleCheck(isAddEndpoint ? 350 : 25, `fetch:${url}`);
      }).catch(() => {});
    } else if (isCartEndpoint && requestStartedInternal) {
      debugEvent('trigger-suppressed', { source: `fetch:${url}`, reason: 'internal-request' });
    }

    return result;
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form && form.action && /\/cart\/add/.test(form.action)) {
      scheduleCheck(300, 'submit:/cart/add');
    }
  });

  document.addEventListener('DOMContentLoaded', () => scheduleCheck(0, 'event:DOMContentLoaded'));
  document.addEventListener('cart:open', () => scheduleCheck(0, 'event:cart:open'));
  document.addEventListener('cart:refresh', () => scheduleCheck(0, 'event:cart:refresh'));
  document.addEventListener('cart:drawer-rendered', () => scheduleCheck(0, 'event:cart:drawer-rendered'));
})();
